/**
 * Merge fields for the outreach templates.
 *
 * The templates already carried `[Fornavn]` and `[Selskap]` as literal text,
 * which meant every rep retyped the same two things before sending — and the
 * mailto link carried the placeholders straight into the mail client. These are
 * now substituted, in the preview and in the mailto, from the lead being
 * looked at and the person looking at it.
 *
 * ## Which tokens exist, and why these
 *
 * Only what can be filled from data the CRM already holds. A token that has to
 * be guessed is worse than no token: it looks filled in.
 *
 *   [Navn] [Fornavn]   the contact's first name
 *   [Fullt navn]       the contact's full name
 *   [Selskap]          the company
 *   [Avsender]         the signed-in rep's name
 *   [Tittel]           their job title
 *   [Avsender e-post]  their address
 *
 * ## What happens when something is missing
 *
 * A missing *contact* name leaves `[Navn]` standing. That is deliberate and
 * loud: the rep reads the preview before sending, and a visible placeholder is
 * caught, while a silent "Hei ," is not.
 *
 * A missing *title* removes its whole line instead. A signature without a title
 * is ordinary; a signature with an empty line where the title should be is not.
 */
import type { Contact, CrmUser, Lead } from '@/types/crm'

export interface MergeContext {
  lead: Lead
  /** Usually the first contact — whoever the mailto is addressed to. */
  contact: Contact | null
  sender: CrmUser | null
}

/** First word of a name, which is what a Norwegian greeting wants. */
function firstName(full: string): string {
  return full.trim().split(/\s+/)[0] ?? ''
}

export function mergeFields(ctx: MergeContext): Record<string, string> {
  const name = ctx.contact?.name?.trim() ?? ''
  return {
    Navn: firstName(name),
    Fornavn: firstName(name),
    'Fullt navn': name,
    Selskap: ctx.lead.name,
    Avsender: ctx.sender?.displayName ?? '',
    Tittel: ctx.sender?.title ?? '',
    'Avsender e-post': ctx.sender?.email ?? '',
  }
}

/**
 * Substitute the tokens in one string.
 *
 * A token whose value is empty is left in place, so the gap is visible — except
 * where `dropEmptyLines` applies, which removes the line entirely. That is used
 * for the signature, where a blank title should not leave a hole.
 */
export function applyMerge(
  text: string,
  fields: Record<string, string>,
  options: { dropEmptyLines?: string[] } = {},
): string {
  const drop = new Set(options.dropEmptyLines ?? [])

  const lines = text.split('\n').filter((line) => {
    // Drop a line that consists only of a token we were told to collapse and
    // whose value is empty.
    const only = /^\s*\[([^\]]+)\]\s*$/.exec(line)
    return !(only && drop.has(only[1]) && !fields[only[1]]?.trim())
  })

  return lines
    .join('\n')
    .replace(/\[([^\]\n]+)\]/g, (match, token: string) => {
      const value = fields[token]
      // Unknown tokens are left alone — `[du/noen hos dere]` is a choice for the
      // rep to make, not a field, and mangling it would be worse than leaving it.
      if (value === undefined) return match
      return value.trim() ? value : match
    })
}

export interface MergedTemplate {
  subjectNo: string
  bodyNo: string
  subjectEn: string
  bodyEn: string
  /** Tokens still unresolved after the merge — shown as a warning above the preview. */
  missing: string[]
}

const SIGNATURE_TOKENS = ['Tittel', 'Avsender e-post']

export function mergeTemplate(
  template: { subjectNo: string; bodyNo: string; subjectEn: string; bodyEn: string },
  ctx: MergeContext,
): MergedTemplate {
  const fields = mergeFields(ctx)
  const opts = { dropEmptyLines: SIGNATURE_TOKENS }

  const out = {
    subjectNo: applyMerge(template.subjectNo, fields, opts),
    bodyNo: applyMerge(template.bodyNo, fields, opts),
    subjectEn: applyMerge(template.subjectEn, fields, opts),
    bodyEn: applyMerge(template.bodyEn, fields, opts),
  }

  const missing = new Set<string>()
  for (const text of Object.values(out)) {
    for (const m of text.matchAll(/\[([^\]\n]+)\]/g)) {
      if (m[1] in fields) missing.add(m[1])
    }
  }

  return { ...out, missing: [...missing] }
}
